import { PrineConvertExportData } from './types/PrineConvertExportData';
import { PrineConvertImportData } from './types/PrineConvertImportData';
import { PrineConvertAdapterImport } from './types/PrineConvertAdapterImport';
import { PrineConvertAdapterExport } from './PrineConvertAdapterExport';
import { PrineConvertDataContainer } from './types/PrineConvertDataContainer';
import { PrineConvertAdapterOptions } from './types/PrineConvertAdapterOptions';
import { PrineConvertAdapterCasts } from './types/PrineConvertAdapterCasts';

export abstract class PrineConvertAdapter {
    protected options: PrineConvertAdapterOptions;
    protected casts: PrineConvertAdapterCasts;

    private defaultBoolValues = [true, false, 'true', 'false'];

    abstract export(data: PrineConvertExportData): PrineConvertAdapterExport[];

    abstract import(data: PrineConvertImportData): PrineConvertAdapterImport[];

    protected getType(container, index, cell) {
        const forcedTypes = container.fields.reduce((previous, current, i) => {
            if (current !== null && typeof current === 'object' && current.type) {
                previous[i] = current.type;
            }
            return previous;
        }, {});

        if (index in forcedTypes) {
            return forcedTypes[index];
        }

        const boolValues = this.options.boolValues ? this.options.boolValues : [];

        // The order is important
        switch (true) {
            // Undefined doesn't work, since no js iterator returns the key of an undefined object cell,
            // investigating further

            case cell === undefined || typeof cell === 'undefined':
                return 'undefined';

            case cell === null:
                return 'null';

            case [...this.defaultBoolValues, ...boolValues].includes(cell):
                return 'bool';

            case !isNaN(cell):
                return 'number';

            case Array.isArray(cell):
                return 'array';

            case typeof cell === 'object':
                return 'object';

            case typeof cell === 'symbol':
                return 'symbol';

            case typeof cell === 'function':
                return 'function';

            default:
                return 'string';
        }
    }

    protected castValues(container: PrineConvertDataContainer) {
        const clone = JSON.parse(JSON.stringify(container));

        clone.rows = container.rows.map((row) =>
            row.map((cell, index) => {
                const type = this.getType(container, index, cell);

                return this.cast(type, cell);
            })
        );

        return clone;
    }

    protected getFieldLabel(field) {
        return field !== null && typeof field === 'object' && field.label ? field.label : field;
    }

    private cast(type, cell) {
        if (this.casts && this.casts[type]) {
            return this.casts[type](cell);
        }

        return cell;
    }
}
